import { applyUserMessageRailUpdate } from "@t3tools/shared/userMessageRail";
import type { UserMessageRailEntry, UserMessageRailUpdate } from "@t3tools/contracts";
import type {
  ActivityCursor,
  EnvironmentId,
  MessageCursor,
  MessageId,
  ClientOrchestrationEvent,
  OrchestrationLatestTurn,
  OrchestrationThreadBlock,
  OrchestrationLiveMessage,
  OrchestrationLiveMessageFrame,
  OrchestrationMessage,
  ClientOrchestrationReadModel,
  ClientOrchestrationShellSnapshot,
  ClientOrchestrationShellStreamEvent,
  ClientOrchestrationSession,
  OrchestrationSessionStatus,
  ClientOrchestrationThread,
  OrchestrationThreadActivityWindow,
  OrchestrationThreadMessageWindow,
  ClientOrchestrationThreadShell,
  ClientOrchestrationThreadActivity,
  ProjectId,
  ScopedProjectRef,
  ScopedThreadRef,
} from "@t3tools/contracts";
import type { ThreadId, TurnId } from "@t3tools/contracts";
import { laterIsoTimestamp } from "@t3tools/shared/orchestrationTiming";
import {
  applyLiveMessageFrame,
  clearLiveMessage,
  EMPTY_LIVE_MESSAGE_OVERLAY,
  liveMessageOverlayFromSnapshot,
  type LiveMessageOverlay,
} from "@t3tools/client-runtime";
import * as Order from "effect/Order";
import { create } from "zustand";
import {
  type ChatMessage,
  type Project,
  type SidebarThreadSummary,
  type Thread,
  type ThreadSession,
  type ThreadShell,
  type ThreadTurnState,
} from "./types";
import { resolveEnvironmentHttpUrl } from "./environments/runtime";
import { sanitizeThreadErrorMessage } from "./rpc/transportError";
import { deriveChildTaskStatus, type ChildTaskStatus } from "./session-logic";
import { getThreadFromEnvironmentState, withLiveMessages } from "./threadDerivation";

export interface MessageWindowState {
  hasMoreBefore: boolean;
  oldestCursor: MessageCursor | null;
  loadingOlder: boolean;
}

export interface ActivityWindowState {
  hasMoreBefore: boolean;
  oldestCursor: ActivityCursor | null;
  loadingOlder: boolean;
}

export interface EnvironmentState {
  projectIds: ProjectId[];
  projectById: Record<ProjectId, Project>;

  // TODO(CLIENT-RUNTIME MIGRATION - DO NOT EXPAND THIS WEB-ONLY COPY):
  // Web still stores shell snapshots and thread details in this denormalized
  // Zustand shape. Mobile uses createShellSnapshotManager and
  // createThreadDetailManager from @t3tools/client-runtime. New shared behavior
  // belongs in those managers/reducers, with a web adapter layered on top.
  //
  // ---------------------------------------------------------------------------
  // Thread bookkeeping — written by BOTH shell stream and detail stream.
  // Both streams ensure the thread is registered here; the bookkeeping is
  // additive (append-only IDs) so concurrent writes are safe.
  // ---------------------------------------------------------------------------
  threadIds: ThreadId[];
  threadIdsByProjectId: Record<ProjectId, ThreadId[]>;

  // ---------------------------------------------------------------------------
  // Thread shell / session / turn — written by BOTH shell stream and detail
  // stream.  The shell stream is the *authoritative* source (server pre-
  // computes these from the projection pipeline), but the detail stream also
  // writes them so the active thread has up-to-date state even if the shell
  // event hasn't arrived yet.  Structural equality checks in both write
  // functions prevent unnecessary React re-renders when both streams deliver
  // equivalent data.
  // ---------------------------------------------------------------------------
  threadShellById: Record<ThreadId, ThreadShell>;
  threadSessionById: Record<ThreadId, ThreadSession | null>;
  threadTurnStateById: Record<ThreadId, ThreadTurnState>;

  // ---------------------------------------------------------------------------
  // Thread detail content — written ONLY by the detail stream
  // (writeThreadState / syncServerThreadDetail).  The shell stream never
  // touches these.
  // ---------------------------------------------------------------------------
  messageIdsByThreadId: Record<ThreadId, MessageId[]>;
  messageByThreadId: Record<ThreadId, Record<MessageId, ChatMessage>>;
  messageWindowByThreadId: Record<ThreadId, MessageWindowState>;
  userMessageRailByThreadId: Record<ThreadId, ReadonlyArray<UserMessageRailEntry>>;
  /**
   * Assistant text that is still streaming. It is transport-only state, kept
   * apart from the projected messages and combined with them when a thread is
   * read; see `@t3tools/client-runtime/liveMessages`.
   */
  liveMessagesByThreadId: Record<ThreadId, LiveMessageOverlay>;
  activityIdsByThreadId: Record<ThreadId, string[]>;
  activityByThreadId: Record<ThreadId, Record<string, ClientOrchestrationThreadActivity>>;
  activityWindowByThreadId: Record<ThreadId, ActivityWindowState>;

  // ---------------------------------------------------------------------------
  // Sidebar summary — written ONLY by the shell stream
  // (writeThreadShellState / mapThreadShell).  Pre-computed server-side with
  // fields like latestUserMessageAt.  The detail
  // stream must NOT write here; the shell stream is the single source of
  // truth for sidebar data.
  // ---------------------------------------------------------------------------
  sidebarThreadSummaryById: Record<ThreadId, SidebarThreadSummary>;

  bootstrapComplete: boolean;
}

export interface AppState {
  activeEnvironmentId: EnvironmentId | null;
  environmentStateById: Record<string, EnvironmentState>;
}

const initialEnvironmentState: EnvironmentState = {
  projectIds: [],
  projectById: {},
  threadIds: [],
  threadIdsByProjectId: {},
  threadShellById: {},
  threadSessionById: {},
  threadTurnStateById: {},
  messageIdsByThreadId: {},
  messageByThreadId: {},
  messageWindowByThreadId: {},
  userMessageRailByThreadId: {},
  liveMessagesByThreadId: {},
  activityIdsByThreadId: {},
  activityByThreadId: {},
  activityWindowByThreadId: {},
  sidebarThreadSummaryById: {},
  bootstrapComplete: false,
};

const initialState: AppState = {
  activeEnvironmentId: null,
  environmentStateById: {},
};

const MAX_THREAD_MESSAGES = 2_000;
const MAX_THREAD_ACTIVITIES = 4_000;

function capThreadActivities(
  sorted: ReadonlyArray<ClientOrchestrationThreadActivity>,
): ClientOrchestrationThreadActivity[] {
  return sorted.slice(Math.max(0, sorted.length - MAX_THREAD_ACTIVITIES));
}
const EMPTY_THREAD_IDS: ThreadId[] = [];

function arraysEqual<T>(left: readonly T[], right: readonly T[]): boolean {
  return left.length === right.length && left.every((value, index) => value === right[index]);
}

function mapSession(session: ClientOrchestrationSession): ThreadSession {
  return {
    status: toLegacySessionStatus(session.status),
    orchestrationStatus: session.status,
    activeTurnId: session.activeTurnId ?? undefined,
    createdAt: session.updatedAt,
    updatedAt: session.updatedAt,
    ...(session.lastError ? { lastError: session.lastError } : {}),
  };
}

export function mapMessage(
  environmentId: EnvironmentId,
  message: OrchestrationMessage,
): ChatMessage {
  const attachments = message.attachments?.map((attachment) =>
    attachment.type === "image"
      ? {
          type: "image" as const,
          id: attachment.id,
          name: attachment.name,
          mimeType: attachment.mimeType,
          sizeBytes: attachment.sizeBytes,
          previewUrl: resolveEnvironmentHttpUrl({
            environmentId,
            pathname: attachmentPreviewRoutePath(attachment.id),
          }),
        }
      : {
          type: "file" as const,
          id: attachment.id,
          name: attachment.name,
          mimeType: attachment.mimeType,
          sizeBytes: attachment.sizeBytes,
        },
  );

  return {
    id: message.id,
    role: message.role,
    text: message.text,
    turnId: message.turnId,
    ...(message.sequence !== undefined ? { sequence: message.sequence } : {}),
    ...(message.phase !== undefined ? { phase: message.phase } : {}),
    createdAt: message.createdAt,
    streaming: message.streaming,
    ...(message.streaming ? {} : { completedAt: message.updatedAt }),
    ...(attachments && attachments.length > 0 ? { attachments } : {}),
  };
}

function mapProject(
  project:
    | ClientOrchestrationReadModel["projects"][number]
    | ClientOrchestrationShellSnapshot["projects"][number],
  environmentId: EnvironmentId,
): Project {
  return {
    id: project.id,
    environmentId,
    name: project.title,
    cwd: project.workspaceRoot,
    createdAt: project.createdAt,
    updatedAt: project.updatedAt,
  };
}

function mapThread(thread: ClientOrchestrationThread, environmentId: EnvironmentId): Thread {
  return {
    id: thread.id,
    environmentId,
    codexThreadId: null,
    projectId: thread.projectId,
    title: thread.title,
    threadKind: thread.threadKind,
    parentThreadId: thread.parentThreadId,
    session: thread.session ? mapSession(thread.session) : null,
    messages: thread.messages.map((message) => mapMessage(environmentId, message)),
    error: sanitizeThreadErrorMessage(thread.session?.lastError),
    createdAt: thread.createdAt,
    archivedAt: thread.archivedAt,
    block: thread.block,
    updatedAt: thread.updatedAt,
    latestTurn: thread.latestTurn,
    activities: thread.activities.map((activity) => ({ ...activity })),
  };
}

function mapThreadShell(
  thread: ClientOrchestrationThreadShell,
  environmentId: EnvironmentId,
): {
  shell: ThreadShell;
  session: ThreadSession | null;
  turnState: ThreadTurnState;
  summary: SidebarThreadSummary;
} {
  const shell: ThreadShell = {
    id: thread.id,
    environmentId,
    codexThreadId: null,
    projectId: thread.projectId,
    title: thread.title,
    threadKind: thread.threadKind,
    parentThreadId: thread.parentThreadId,
    error: sanitizeThreadErrorMessage(thread.session?.lastError),
    createdAt: thread.createdAt,
    archivedAt: thread.archivedAt,
    block: thread.block,
    updatedAt: thread.updatedAt,
  };
  const session = thread.session ? mapSession(thread.session) : null;
  const turnState: ThreadTurnState = {
    latestTurn: thread.latestTurn,
  };
  const summary: SidebarThreadSummary = {
    id: thread.id,
    environmentId,
    projectId: thread.projectId,
    title: thread.title,
    session,
    createdAt: thread.createdAt,
    archivedAt: thread.archivedAt,
    block: thread.block,
    updatedAt: thread.updatedAt,
    latestTurn: thread.latestTurn,
    latestUserMessageAt: thread.latestUserMessageAt,
  };
  return {
    shell,
    session,
    turnState,
    summary,
  };
}

function toThreadShell(thread: Thread): ThreadShell {
  return {
    id: thread.id,
    environmentId: thread.environmentId,
    codexThreadId: thread.codexThreadId,
    projectId: thread.projectId,
    title: thread.title,
    ...(thread.threadKind !== undefined ? { threadKind: thread.threadKind } : {}),
    ...(thread.parentThreadId !== undefined ? { parentThreadId: thread.parentThreadId } : {}),
    error: thread.error,
    createdAt: thread.createdAt,
    archivedAt: thread.archivedAt,
    block: thread.block,
    updatedAt: thread.updatedAt,
  };
}

function toThreadTurnState(thread: Thread): ThreadTurnState {
  return {
    latestTurn: thread.latestTurn,
  };
}

function latestTurnsEqual(
  left: OrchestrationLatestTurn | null | undefined,
  right: OrchestrationLatestTurn | null | undefined,
): boolean {
  if (left === right) return true;
  if (left == null || right == null) return false;
  return (
    left.turnId === right.turnId &&
    left.state === right.state &&
    left.requestedAt === right.requestedAt &&
    left.startedAt === right.startedAt &&
    left.completedAt === right.completedAt &&
    left.assistantMessageId === right.assistantMessageId
  );
}

function threadSessionsEqual(
  left: ThreadSession | null | undefined,
  right: ThreadSession | null | undefined,
): boolean {
  if (left === right) return true;
  if (left == null || right == null) return false;
  return (
    left.status === right.status &&
    left.orchestrationStatus === right.orchestrationStatus &&
    left.activeTurnId === right.activeTurnId &&
    left.createdAt === right.createdAt &&
    left.updatedAt === right.updatedAt &&
    left.lastError === right.lastError
  );
}

function sidebarThreadSummariesEqual(
  left: SidebarThreadSummary | undefined,
  right: SidebarThreadSummary,
): boolean {
  return (
    left !== undefined &&
    left.id === right.id &&
    left.projectId === right.projectId &&
    left.title === right.title &&
    threadSessionsEqual(left.session, right.session) &&
    left.createdAt === right.createdAt &&
    left.archivedAt === right.archivedAt &&
    threadBlocksEqual(left.block, right.block) &&
    left.updatedAt === right.updatedAt &&
    latestTurnsEqual(left.latestTurn, right.latestTurn) &&
    left.latestUserMessageAt === right.latestUserMessageAt
  );
}

function threadBlocksEqual(
  left: OrchestrationThreadBlock | null,
  right: OrchestrationThreadBlock | null,
): boolean {
  if (left === right) return true;
  if (left === null || right === null) return false;
  return (
    left.topic === right.topic &&
    left.title === right.title &&
    left.text === right.text &&
    left.blockedAt === right.blockedAt
  );
}

function threadShellsEqual(left: ThreadShell | undefined, right: ThreadShell): boolean {
  return (
    left !== undefined &&
    left.id === right.id &&
    left.environmentId === right.environmentId &&
    left.codexThreadId === right.codexThreadId &&
    left.projectId === right.projectId &&
    left.title === right.title &&
    left.threadKind === right.threadKind &&
    left.parentThreadId === right.parentThreadId &&
    left.error === right.error &&
    left.createdAt === right.createdAt &&
    left.archivedAt === right.archivedAt &&
    threadBlocksEqual(left.block, right.block) &&
    left.updatedAt === right.updatedAt
  );
}

function threadTurnStatesEqual(left: ThreadTurnState | undefined, right: ThreadTurnState): boolean {
  return left !== undefined && latestTurnsEqual(left.latestTurn, right.latestTurn);
}

function appendId<T extends string>(ids: readonly T[], id: T): T[] {
  return ids.includes(id) ? [...ids] : [...ids, id];
}

function removeId<T extends string>(ids: readonly T[], id: T): T[] {
  return ids.filter((value) => value !== id);
}

function buildMessageSlice(thread: Thread): {
  ids: MessageId[];
  byId: Record<MessageId, ChatMessage>;
} {
  return {
    ids: thread.messages.map((message) => message.id),
    byId: Object.fromEntries(
      thread.messages.map((message) => [message.id, message] as const),
    ) as Record<MessageId, ChatMessage>,
  };
}

function mergeActivityPayload(
  incoming: ClientOrchestrationThreadActivity,
  previous: ClientOrchestrationThreadActivity | undefined,
): ClientOrchestrationThreadActivity {
  if (previous !== undefined && previous.payload !== undefined && incoming.payload === undefined) {
    return { ...incoming, payload: previous.payload };
  }
  return incoming;
}

function buildActivitySlice(
  thread: Thread,
  previousById?: Record<string, ClientOrchestrationThreadActivity>,
): {
  ids: string[];
  byId: Record<string, ClientOrchestrationThreadActivity>;
} {
  return {
    ids: thread.activities.map((activity) => activity.id),
    byId: Object.fromEntries(
      thread.activities.map(
        (activity) =>
          [activity.id, mergeActivityPayload(activity, previousById?.[activity.id])] as const,
      ),
    ) as Record<string, ClientOrchestrationThreadActivity>,
  };
}

function getProjects(state: EnvironmentState): Project[] {
  return state.projectIds.flatMap((projectId) => {
    const project = state.projectById[projectId];
    return project ? [project] : [];
  });
}

function getThreads(state: EnvironmentState): Thread[] {
  return state.threadIds.flatMap((threadId) => {
    const thread = getThreadFromEnvironmentState(state, threadId);
    return thread ? [thread] : [];
  });
}

/**
 * Ensure a thread is registered in the bookkeeping indices (threadIds,
 * threadIdsByProjectId).  Shared by both the shell stream and detail stream
 * write paths — the bookkeeping is additive (append-only IDs) so concurrent
 * writes from both streams are safe.
 */
function ensureThreadRegistered(
  state: EnvironmentState,
  threadId: ThreadId,
  nextProjectId: ProjectId,
  previousProjectId: ProjectId | undefined,
): EnvironmentState {
  let nextState = state;

  if (!state.threadIds.includes(threadId)) {
    nextState = {
      ...nextState,
      threadIds: [...nextState.threadIds, threadId],
    };
  }

  if (previousProjectId !== nextProjectId) {
    let threadIdsByProjectId = nextState.threadIdsByProjectId;
    if (previousProjectId) {
      const previousIds = threadIdsByProjectId[previousProjectId] ?? EMPTY_THREAD_IDS;
      const nextIds = removeId(previousIds, threadId);
      if (nextIds.length === 0) {
        const { [previousProjectId]: _removed, ...rest } = threadIdsByProjectId;
        threadIdsByProjectId = rest as Record<ProjectId, ThreadId[]>;
      } else if (!arraysEqual(previousIds, nextIds)) {
        threadIdsByProjectId = {
          ...threadIdsByProjectId,
          [previousProjectId]: nextIds,
        };
      }
    }
    const projectThreadIds = threadIdsByProjectId[nextProjectId] ?? EMPTY_THREAD_IDS;
    const nextProjectThreadIds = appendId(projectThreadIds, threadId);
    if (!arraysEqual(projectThreadIds, nextProjectThreadIds)) {
      threadIdsByProjectId = {
        ...threadIdsByProjectId,
        [nextProjectId]: nextProjectThreadIds,
      };
    }
    if (threadIdsByProjectId !== nextState.threadIdsByProjectId) {
      nextState = {
        ...nextState,
        threadIdsByProjectId,
      };
    }
  }

  return nextState;
}

/**
 * Write the shell half of a thread (shell record, session, turn state, sidebar
 * registration) from a full `Thread`.
 *
 * Only callers holding a thread whose identity came from the store or from a
 * domain event may use this: the shell record carries fields no per-thread
 * payload can reconstruct (parentage, thread kind), so authoring it from a
 * partial source silently overwrites them. See {@link writeThreadDetailState}.
 */
function writeThreadShellHalf(
  state: EnvironmentState,
  nextThread: Thread,
  previousThread?: Thread,
): EnvironmentState {
  const nextShell = toThreadShell(nextThread);
  const nextTurnState = toThreadTurnState(nextThread);
  const previousShell = state.threadShellById[nextThread.id];
  const previousTurnState = state.threadTurnStateById[nextThread.id];

  // Sub-agent threads are addressable by id (side panel) but stay out of the
  // sidebar bookkeeping indices.
  let nextState =
    nextThread.threadKind === "subagent"
      ? state
      : ensureThreadRegistered(
          state,
          nextThread.id,
          nextThread.projectId,
          previousThread?.projectId,
        );

  if (!threadShellsEqual(previousShell, nextShell)) {
    nextState = {
      ...nextState,
      threadShellById: {
        ...nextState.threadShellById,
        [nextThread.id]: nextShell,
      },
    };
  }

  if (!threadSessionsEqual(previousThread?.session ?? null, nextThread.session)) {
    nextState = {
      ...nextState,
      threadSessionById: {
        ...nextState.threadSessionById,
        [nextThread.id]: nextThread.session,
      },
    };
  }

  if (!threadTurnStatesEqual(previousTurnState, nextTurnState)) {
    nextState = {
      ...nextState,
      threadTurnStateById: {
        ...nextState.threadTurnStateById,
        [nextThread.id]: nextTurnState,
      },
    };
  }

  return nextState;
}

/**
 * Write the content half of a thread: messages, activities and turn diff
 * summaries.
 */
function writeThreadContentState(
  state: EnvironmentState,
  nextThread: Thread,
  previousThread?: Thread,
): EnvironmentState {
  let nextState = state;

  if (previousThread?.messages !== nextThread.messages) {
    const nextMessageSlice = buildMessageSlice(nextThread);
    nextState = {
      ...nextState,
      messageIdsByThreadId: {
        ...nextState.messageIdsByThreadId,
        [nextThread.id]: nextMessageSlice.ids,
      },
      messageByThreadId: {
        ...nextState.messageByThreadId,
        [nextThread.id]: nextMessageSlice.byId,
      },
    };
  }

  if (previousThread?.activities !== nextThread.activities) {
    const nextActivitySlice = buildActivitySlice(
      nextThread,
      nextState.activityByThreadId[nextThread.id],
    );
    nextState = {
      ...nextState,
      activityIdsByThreadId: {
        ...nextState.activityIdsByThreadId,
        [nextThread.id]: nextActivitySlice.ids,
      },
      activityByThreadId: {
        ...nextState.activityByThreadId,
        [nextThread.id]: nextActivitySlice.byId,
      },
    };
  }

  return nextState;
}

/**
 * Write a thread whose identity is already trusted: state derived from the
 * store, or a `thread.created` event that carries the full identity.
 */
function writeThreadState(
  state: EnvironmentState,
  nextThread: Thread,
  previousThread?: Thread,
): EnvironmentState {
  return writeThreadContentState(
    writeThreadShellHalf(state, nextThread, previousThread),
    nextThread,
    previousThread,
  );
}

/**
 * Write thread state from the **detail stream** (per-thread subscription).
 *
 * Owns: messages, activities, turn diff summaries. The shell
 * half is written only for a thread the store has never seen — a detail
 * payload is assembled per thread and cannot carry the sidebar's view of it,
 * so letting it author an existing shell would overwrite fields it does not
 * know about with decoded defaults.
 * Does NOT write sidebarThreadSummaryById — that is shell-stream-only.
 */
function writeThreadDetailState(
  state: EnvironmentState,
  nextThread: Thread,
  previousThread?: Thread,
): EnvironmentState {
  const nextState =
    state.threadShellById[nextThread.id] === undefined
      ? writeThreadShellHalf(state, nextThread, previousThread)
      : state;
  return writeThreadContentState(nextState, nextThread, previousThread);
}

/**
 * Write thread state from the **shell stream** (all-threads subscription).
 *
 * Owns: sidebarThreadSummaryById (pre-computed server-side sidebar data) and
 * threadShellById / threadSessionById / threadTurnStateById as the
 * authoritative source for these fields.
 * Does NOT write message/activity content — that is
 * detail-stream-only.
 */
function writeThreadShellState(
  state: EnvironmentState,
  nextThread: {
    shell: ThreadShell;
    session: ThreadSession | null;
    turnState: ThreadTurnState;
    summary: SidebarThreadSummary;
  },
): EnvironmentState {
  const previousShell = state.threadShellById[nextThread.shell.id];

  let nextState =
    nextThread.shell.threadKind === "subagent"
      ? state
      : ensureThreadRegistered(
          state,
          nextThread.shell.id,
          nextThread.shell.projectId,
          previousShell?.projectId,
        );

  if (!threadShellsEqual(previousShell, nextThread.shell)) {
    nextState = {
      ...nextState,
      threadShellById: {
        ...nextState.threadShellById,
        [nextThread.shell.id]: nextThread.shell,
      },
    };
  }

  if (
    !threadSessionsEqual(state.threadSessionById[nextThread.shell.id] ?? null, nextThread.session)
  ) {
    nextState = {
      ...nextState,
      threadSessionById: {
        ...nextState.threadSessionById,
        [nextThread.shell.id]: nextThread.session,
      },
    };
  }

  if (
    !threadTurnStatesEqual(state.threadTurnStateById[nextThread.shell.id], nextThread.turnState)
  ) {
    nextState = {
      ...nextState,
      threadTurnStateById: {
        ...nextState.threadTurnStateById,
        [nextThread.shell.id]: nextThread.turnState,
      },
    };
  }

  if (
    !sidebarThreadSummariesEqual(
      state.sidebarThreadSummaryById[nextThread.shell.id],
      nextThread.summary,
    )
  ) {
    nextState = {
      ...nextState,
      sidebarThreadSummaryById: {
        ...nextState.sidebarThreadSummaryById,
        [nextThread.shell.id]: nextThread.summary,
      },
    };
  }

  return nextState;
}

function retainThreadScopedRecord<T>(
  record: Record<ThreadId, T>,
  nextThreadIds: ReadonlySet<ThreadId>,
): Record<ThreadId, T> {
  return Object.fromEntries(
    Object.entries(record).flatMap(([threadId, value]) =>
      nextThreadIds.has(threadId as ThreadId) ? [[threadId, value] as const] : [],
    ),
  ) as Record<ThreadId, T>;
}

function removeThreadState(state: EnvironmentState, threadId: ThreadId): EnvironmentState {
  const shell = state.threadShellById[threadId];
  if (!shell) {
    return state;
  }

  const nextThreadIds = removeId(state.threadIds, threadId);
  const currentProjectThreadIds = state.threadIdsByProjectId[shell.projectId] ?? EMPTY_THREAD_IDS;
  const nextProjectThreadIds = removeId(currentProjectThreadIds, threadId);
  const nextThreadIdsByProjectId =
    nextProjectThreadIds.length === 0
      ? (() => {
          const { [shell.projectId]: _removed, ...rest } = state.threadIdsByProjectId;
          return rest as Record<ProjectId, ThreadId[]>;
        })()
      : {
          ...state.threadIdsByProjectId,
          [shell.projectId]: nextProjectThreadIds,
        };

  const { [threadId]: _removedShell, ...threadShellById } = state.threadShellById;
  const { [threadId]: _removedSession, ...threadSessionById } = state.threadSessionById;
  const { [threadId]: _removedTurnState, ...threadTurnStateById } = state.threadTurnStateById;
  const { [threadId]: _removedMessageIds, ...messageIdsByThreadId } = state.messageIdsByThreadId;
  const { [threadId]: _removedMessages, ...messageByThreadId } = state.messageByThreadId;
  const { [threadId]: _removedRail, ...userMessageRailByThreadId } =
    state.userMessageRailByThreadId;
  const { [threadId]: _removedMessageWindow, ...messageWindowByThreadId } =
    state.messageWindowByThreadId;
  const { [threadId]: _removedActivityIds, ...activityIdsByThreadId } = state.activityIdsByThreadId;
  const { [threadId]: _removedActivities, ...activityByThreadId } = state.activityByThreadId;
  const { [threadId]: _removedActivityWindow, ...activityWindowByThreadId } =
    state.activityWindowByThreadId;
  const { [threadId]: _removedSidebarSummary, ...sidebarThreadSummaryById } =
    state.sidebarThreadSummaryById;

  return {
    ...state,
    threadIds: nextThreadIds,
    threadIdsByProjectId: nextThreadIdsByProjectId,
    threadShellById,
    threadSessionById,
    threadTurnStateById,
    messageIdsByThreadId,
    messageByThreadId,
    messageWindowByThreadId,
    userMessageRailByThreadId,
    activityIdsByThreadId,
    activityByThreadId,
    activityWindowByThreadId,
    sidebarThreadSummaryById,
  };
}

function compareActivities(
  left: Thread["activities"][number],
  right: Thread["activities"][number],
): number {
  if (left.sequence !== undefined && right.sequence !== undefined) {
    if (left.sequence !== right.sequence) {
      return left.sequence - right.sequence;
    }
  } else if (left.sequence !== undefined) {
    return 1;
  } else if (right.sequence !== undefined) {
    return -1;
  }

  return left.createdAt.localeCompare(right.createdAt) || left.id.localeCompare(right.id);
}

function compareMessages(left: ChatMessage, right: ChatMessage): number {
  if (left.sequence !== undefined && right.sequence !== undefined) {
    if (left.sequence !== right.sequence) {
      return left.sequence - right.sequence;
    }
  } else if (left.sequence !== undefined) {
    return 1;
  } else if (right.sequence !== undefined) {
    return -1;
  }

  return left.createdAt.localeCompare(right.createdAt) || left.id.localeCompare(right.id);
}

function toLegacySessionStatus(
  status: OrchestrationSessionStatus,
): "connecting" | "ready" | "running" | "error" | "closed" {
  switch (status) {
    case "starting":
      return "connecting";
    case "running":
      return "running";
    case "error":
      return "error";
    case "ready":
    case "interrupted":
      return "ready";
    case "idle":
    case "stopped":
      return "closed";
  }
}

function attachmentPreviewRoutePath(attachmentId: string): string {
  // The id is a thread-relative path "<threadSegment>/<fileName>"; encode each
  // segment but keep the "/" separator so it routes to GET /attachments/*.
  const encoded = attachmentId
    .split("/")
    .map((segment) => encodeURIComponent(segment))
    .join("/");
  return `/attachments/${encoded}`;
}

/**
 * Drop a message's live text once its durable text has landed. `messageId` is
 * null while the message is still streaming.
 */
function clearLiveMessageForEnvironment(
  state: EnvironmentState,
  threadId: ThreadId,
  messageId: MessageId | null,
): EnvironmentState {
  if (messageId === null) {
    return state;
  }
  const overlay = state.liveMessagesByThreadId[threadId];
  if (overlay === undefined) {
    return state;
  }
  const nextOverlay = clearLiveMessage(overlay, messageId);
  return nextOverlay === overlay
    ? state
    : {
        ...state,
        liveMessagesByThreadId: { ...state.liveMessagesByThreadId, [threadId]: nextOverlay },
      };
}

function updateThreadState(
  state: EnvironmentState,
  threadId: ThreadId,
  updater: (thread: Thread) => Thread,
): EnvironmentState {
  const currentThread = getThreadFromEnvironmentState(state, threadId);
  if (!currentThread) {
    return state;
  }
  const nextThread = updater(currentThread);
  if (nextThread === currentThread) {
    return state;
  }
  return writeThreadState(state, nextThread, currentThread);
}

function buildProjectState(
  projects: ReadonlyArray<Project>,
): Pick<EnvironmentState, "projectIds" | "projectById"> {
  return {
    projectIds: projects.map((project) => project.id),
    projectById: Object.fromEntries(
      projects.map((project) => [project.id, project] as const),
    ) as Record<ProjectId, Project>,
  };
}

function getStoredEnvironmentState(
  state: AppState,
  environmentId: EnvironmentId,
): EnvironmentState {
  return state.environmentStateById[environmentId] ?? initialEnvironmentState;
}

function commitEnvironmentState(
  state: AppState,
  environmentId: EnvironmentId,
  nextEnvironmentState: EnvironmentState,
): AppState {
  const currentEnvironmentState = state.environmentStateById[environmentId];
  const environmentStateById =
    currentEnvironmentState === nextEnvironmentState
      ? state.environmentStateById
      : {
          ...state.environmentStateById,
          [environmentId]: nextEnvironmentState,
        };

  if (environmentStateById === state.environmentStateById) {
    return state;
  }

  return {
    ...state,
    environmentStateById,
  };
}

function syncEnvironmentShellSnapshot(
  state: EnvironmentState,
  snapshot: ClientOrchestrationShellSnapshot,
  environmentId: EnvironmentId,
): EnvironmentState {
  const nextProjects = snapshot.projects.map((project) => mapProject(project, environmentId));
  const nextThreadIds = new Set(snapshot.threads.map((thread) => thread.id));
  let nextState: EnvironmentState = {
    ...state,
    ...buildProjectState(nextProjects),
    threadIds: [],
    threadIdsByProjectId: {},
    threadShellById: {},
    threadSessionById: {},
    threadTurnStateById: {},
    sidebarThreadSummaryById: {},
    messageIdsByThreadId: retainThreadScopedRecord(state.messageIdsByThreadId, nextThreadIds),
    messageByThreadId: retainThreadScopedRecord(state.messageByThreadId, nextThreadIds),
    messageWindowByThreadId: retainThreadScopedRecord(state.messageWindowByThreadId, nextThreadIds),
    userMessageRailByThreadId: retainThreadScopedRecord(
      state.userMessageRailByThreadId,
      nextThreadIds,
    ),
    liveMessagesByThreadId: retainThreadScopedRecord(state.liveMessagesByThreadId, nextThreadIds),
    activityIdsByThreadId: retainThreadScopedRecord(state.activityIdsByThreadId, nextThreadIds),
    activityByThreadId: retainThreadScopedRecord(state.activityByThreadId, nextThreadIds),
    activityWindowByThreadId: retainThreadScopedRecord(
      state.activityWindowByThreadId,
      nextThreadIds,
    ),
    bootstrapComplete: true,
  };

  for (const thread of snapshot.threads) {
    nextState = writeThreadShellState(nextState, mapThreadShell(thread, environmentId));
  }

  return nextState;
}

export function syncServerShellSnapshot(
  state: AppState,
  snapshot: ClientOrchestrationShellSnapshot,
  environmentId: EnvironmentId,
): AppState {
  // TODO(CLIENT-RUNTIME MIGRATION - DO NOT EXPAND THIS WEB-ONLY COPY):
  // Keep web-specific projection here only until the store can consume
  // createShellSnapshotManager or a shared adapter over its reducer.
  return commitEnvironmentState(
    state,
    environmentId,
    syncEnvironmentShellSnapshot(
      getStoredEnvironmentState(state, environmentId),
      snapshot,
      environmentId,
    ),
  );
}

export function syncServerThreadDetail(
  state: AppState,
  thread: ClientOrchestrationThread,
  environmentId: EnvironmentId,
  messageWindow: OrchestrationThreadMessageWindow,
  activityWindow: OrchestrationThreadActivityWindow,
  liveMessages: ReadonlyArray<OrchestrationLiveMessage>,
): AppState {
  // TODO(CLIENT-RUNTIME MIGRATION - DO NOT EXPAND THIS WEB-ONLY COPY):
  // Keep web-specific projection here only until the store can consume
  // createThreadDetailManager or a shared adapter over its reducer.
  const environmentState = getStoredEnvironmentState(state, environmentId);
  const previousThread = getThreadFromEnvironmentState(environmentState, thread.id);
  const nextEnv = writeThreadDetailState(
    environmentState,
    mapThread(thread, environmentId),
    previousThread,
  );
  return commitEnvironmentState(state, environmentId, {
    ...nextEnv,
    messageWindowByThreadId: {
      ...nextEnv.messageWindowByThreadId,
      [thread.id]: {
        hasMoreBefore: messageWindow.hasMoreBefore,
        oldestCursor: messageWindow.oldestCursor,
        loadingOlder: false,
      },
    },
    activityWindowByThreadId: {
      ...nextEnv.activityWindowByThreadId,
      [thread.id]: {
        hasMoreBefore: activityWindow.hasMoreBefore,
        oldestCursor: activityWindow.oldestCursor,
        loadingOlder: false,
      },
    },
    liveMessagesByThreadId: {
      ...nextEnv.liveMessagesByThreadId,
      [thread.id]: liveMessageOverlayFromSnapshot(liveMessages),
    },
  });
}

export function applyLiveMessageFrameToState(
  state: AppState,
  environmentId: EnvironmentId,
  frame: OrchestrationLiveMessageFrame,
): AppState {
  const environmentState = getStoredEnvironmentState(state, environmentId);
  const overlay =
    environmentState.liveMessagesByThreadId[frame.threadId] ?? EMPTY_LIVE_MESSAGE_OVERLAY;
  const nextOverlay = applyLiveMessageFrame(overlay, frame);
  if (nextOverlay === overlay) {
    return state;
  }
  return commitEnvironmentState(state, environmentId, {
    ...environmentState,
    liveMessagesByThreadId: {
      ...environmentState.liveMessagesByThreadId,
      [frame.threadId]: nextOverlay,
    },
  });
}

export function prependOlderMessages(
  state: AppState,
  threadId: ThreadId,
  environmentId: EnvironmentId,
  page: { messages: ChatMessage[]; hasMoreBefore: boolean; oldestCursor: MessageCursor | null },
): AppState {
  const env = getStoredEnvironmentState(state, environmentId);
  const existingIds = env.messageIdsByThreadId[threadId] ?? [];
  const existing = new Set(existingIds);
  const newOnes = page.messages.filter((m) => !existing.has(m.id));
  const nextIds = [...newOnes.map((m) => m.id), ...existingIds];
  const nextById = { ...env.messageByThreadId[threadId] };
  for (const m of newOnes) {
    nextById[m.id] = m;
  }
  return commitEnvironmentState(state, environmentId, {
    ...env,
    messageIdsByThreadId: { ...env.messageIdsByThreadId, [threadId]: nextIds },
    messageByThreadId: { ...env.messageByThreadId, [threadId]: nextById },
    messageWindowByThreadId: {
      ...env.messageWindowByThreadId,
      [threadId]: {
        hasMoreBefore: page.hasMoreBefore,
        oldestCursor: page.oldestCursor,
        loadingOlder: false,
      },
    },
  });
}

export function beginLoadingOlder(
  state: AppState,
  threadId: ThreadId,
  environmentId: EnvironmentId,
): AppState {
  const env = getStoredEnvironmentState(state, environmentId);
  const current = env.messageWindowByThreadId[threadId];
  if (!current) {
    return state;
  }
  return commitEnvironmentState(state, environmentId, {
    ...env,
    messageWindowByThreadId: {
      ...env.messageWindowByThreadId,
      [threadId]: { ...current, loadingOlder: true },
    },
  });
}

export function prependOlderActivities(
  state: AppState,
  threadId: ThreadId,
  environmentId: EnvironmentId,
  page: {
    activities: ClientOrchestrationThreadActivity[];
    hasMoreBefore: boolean;
    oldestCursor: ActivityCursor | null;
  },
): AppState {
  const env = getStoredEnvironmentState(state, environmentId);
  const existingIds = env.activityIdsByThreadId[threadId] ?? [];
  const existing = new Set(existingIds);
  const newOnes = page.activities.filter((a) => !existing.has(a.id));
  const nextIds = [...newOnes.map((a) => a.id), ...existingIds];
  const nextById = { ...env.activityByThreadId[threadId] };
  for (const a of newOnes) {
    nextById[a.id] = mergeActivityPayload(a, nextById[a.id]);
  }
  return commitEnvironmentState(state, environmentId, {
    ...env,
    activityIdsByThreadId: { ...env.activityIdsByThreadId, [threadId]: nextIds },
    activityByThreadId: { ...env.activityByThreadId, [threadId]: nextById },
    activityWindowByThreadId: {
      ...env.activityWindowByThreadId,
      [threadId]: {
        hasMoreBefore: page.hasMoreBefore,
        oldestCursor: page.oldestCursor,
        loadingOlder: false,
      },
    },
  });
}

export function beginLoadingOlderActivities(
  state: AppState,
  threadId: ThreadId,
  environmentId: EnvironmentId,
): AppState {
  const env = getStoredEnvironmentState(state, environmentId);
  const current = env.activityWindowByThreadId[threadId];
  if (!current) {
    return state;
  }
  return commitEnvironmentState(state, environmentId, {
    ...env,
    activityWindowByThreadId: {
      ...env.activityWindowByThreadId,
      [threadId]: { ...current, loadingOlder: true },
    },
  });
}

export function setActivityPayload(
  state: AppState,
  threadId: ThreadId,
  environmentId: EnvironmentId,
  activityId: string,
  payload: unknown,
): AppState {
  const env = getStoredEnvironmentState(state, environmentId);
  const threadActivities = env.activityByThreadId[threadId];
  const existing = threadActivities?.[activityId];
  if (!existing) {
    return state;
  }
  return commitEnvironmentState(state, environmentId, {
    ...env,
    activityByThreadId: {
      ...env.activityByThreadId,
      [threadId]: {
        ...threadActivities,
        [activityId]: { ...existing, payload },
      },
    },
  });
}

export function clearThreadDetail(
  state: AppState,
  threadId: ThreadId,
  environmentId: EnvironmentId,
): AppState {
  const env = getStoredEnvironmentState(state, environmentId);
  if (
    env.messageIdsByThreadId[threadId] === undefined &&
    env.activityIdsByThreadId[threadId] === undefined &&
    env.liveMessagesByThreadId[threadId] === undefined &&
    env.userMessageRailByThreadId[threadId] === undefined
  ) {
    return state;
  }
  const { [threadId]: _messageIds, ...messageIdsByThreadId } = env.messageIdsByThreadId;
  const { [threadId]: _messages, ...messageByThreadId } = env.messageByThreadId;
  const { [threadId]: _rail, ...userMessageRailByThreadId } = env.userMessageRailByThreadId;
  const { [threadId]: _messageWindow, ...messageWindowByThreadId } = env.messageWindowByThreadId;
  const { [threadId]: _liveMessages, ...liveMessagesByThreadId } = env.liveMessagesByThreadId;
  const { [threadId]: _activityIds, ...activityIdsByThreadId } = env.activityIdsByThreadId;
  const { [threadId]: _activities, ...activityByThreadId } = env.activityByThreadId;
  const { [threadId]: _activityWindow, ...activityWindowByThreadId } = env.activityWindowByThreadId;
  return commitEnvironmentState(state, environmentId, {
    ...env,
    messageIdsByThreadId,
    messageByThreadId,
    messageWindowByThreadId,
    userMessageRailByThreadId,
    liveMessagesByThreadId,
    activityIdsByThreadId,
    activityByThreadId,
    activityWindowByThreadId,
  });
}

function applyEnvironmentOrchestrationEvent(
  state: EnvironmentState,
  event: ClientOrchestrationEvent,
  environmentId: EnvironmentId,
): EnvironmentState {
  switch (event.type) {
    case "project.created": {
      const nextProject = mapProject(
        {
          id: event.payload.projectId,
          title: event.payload.title,
          workspaceRoot: event.payload.workspaceRoot,
          createdAt: event.payload.createdAt,
          updatedAt: event.payload.updatedAt,
          deletedAt: null,
        },
        environmentId,
      );
      const existingProjectId =
        state.projectIds.find(
          (projectId) =>
            projectId === event.payload.projectId ||
            state.projectById[projectId]?.cwd === event.payload.workspaceRoot,
        ) ?? null;
      let projectById = state.projectById;
      let projectIds = state.projectIds;

      if (existingProjectId !== null && existingProjectId !== nextProject.id) {
        const { [existingProjectId]: _removedProject, ...restProjectById } = state.projectById;
        projectById = {
          ...restProjectById,
          [nextProject.id]: nextProject,
        };
        projectIds = state.projectIds.map((projectId) =>
          projectId === existingProjectId ? nextProject.id : projectId,
        );
      } else {
        projectById = {
          ...state.projectById,
          [nextProject.id]: nextProject,
        };
        projectIds =
          existingProjectId === null && !state.projectIds.includes(nextProject.id)
            ? [...state.projectIds, nextProject.id]
            : state.projectIds;
      }

      return {
        ...state,
        projectById,
        projectIds,
      };
    }

    case "project.meta-updated": {
      const project = state.projectById[event.payload.projectId];
      if (!project) {
        return state;
      }
      const nextProject: Project = {
        ...project,
        ...(event.payload.title !== undefined ? { name: event.payload.title } : {}),
        ...(event.payload.workspaceRoot !== undefined ? { cwd: event.payload.workspaceRoot } : {}),
        updatedAt: event.payload.updatedAt,
      };
      return {
        ...state,
        projectById: {
          ...state.projectById,
          [event.payload.projectId]: nextProject,
        },
      };
    }

    case "project.deleted": {
      if (!state.projectById[event.payload.projectId]) {
        return state;
      }
      const { [event.payload.projectId]: _removedProject, ...projectById } = state.projectById;
      return {
        ...state,
        projectById,
        projectIds: removeId(state.projectIds, event.payload.projectId),
      };
    }

    case "thread.created": {
      const previousThread = getThreadFromEnvironmentState(state, event.payload.threadId);
      const nextThread = mapThread(
        {
          id: event.payload.threadId,
          projectId: event.payload.projectId,
          title: event.payload.title,
          threadKind: event.payload.threadKind,
          parentThreadId: event.payload.parentThreadId,
          latestTurn: null,
          createdAt: event.payload.createdAt,
          updatedAt: event.payload.updatedAt,
          archivedAt: null,
          block: null,
          deletedAt: null,
          messages: [],
          activities: [],
          session: null,
        },
        environmentId,
      );
      return writeThreadState(state, nextThread, previousThread);
    }

    case "thread.deleted":
      return removeThreadState(state, event.payload.threadId);

    case "thread.archived":
      return updateThreadState(state, event.payload.threadId, (thread) => ({
        ...thread,
        archivedAt: event.payload.archivedAt,
        updatedAt: event.payload.updatedAt,
      }));

    case "thread.unarchived":
      return updateThreadState(state, event.payload.threadId, (thread) => ({
        ...thread,
        archivedAt: null,
        updatedAt: event.payload.updatedAt,
      }));

    case "thread.blocked":
      return updateThreadState(state, event.payload.threadId, (thread) => ({
        ...thread,
        block: event.payload.block,
        updatedAt: event.payload.block.blockedAt,
      }));

    case "thread.unblocked":
      return updateThreadState(state, event.payload.threadId, (thread) => ({
        ...thread,
        block: null,
        updatedAt: event.payload.updatedAt,
      }));

    case "thread.meta-updated":
      return updateThreadState(state, event.payload.threadId, (thread) => ({
        ...thread,
        ...(event.payload.title !== undefined ? { title: event.payload.title } : {}),
        updatedAt: event.payload.updatedAt,
      }));

    case "thread.turn-start-requested":
      return updateThreadState(state, event.payload.threadId, (thread) => ({
        ...thread,
        updatedAt: event.occurredAt,
      }));

    case "thread.turn-interrupt-requested": {
      if (event.payload.turnId === undefined) {
        return state;
      }
      return updateThreadState(state, event.payload.threadId, (thread) => {
        const latestTurn = thread.latestTurn;
        if (latestTurn === null || latestTurn.turnId !== event.payload.turnId) {
          return thread;
        }
        return {
          ...thread,
          latestTurn: {
            turnId: event.payload.turnId,
            state: "interrupted",
            requestedAt: latestTurn.requestedAt,
            startedAt: latestTurn.startedAt ?? event.payload.createdAt,
            completedAt: laterIsoTimestamp(latestTurn.completedAt, event.payload.createdAt),
            assistantMessageId: latestTurn.assistantMessageId,
          },
          updatedAt: event.occurredAt,
        };
      });
    }

    case "thread.message-sent":
      return clearLiveMessageForEnvironment(
        updateThreadState(state, event.payload.threadId, (thread) => {
          const message = mapMessage(thread.environmentId, {
            id: event.payload.messageId,
            role: event.payload.role,
            text: event.payload.text,
            ...(event.payload.attachments !== undefined
              ? { attachments: event.payload.attachments }
              : {}),
            turnId: event.payload.turnId,
            streaming: event.payload.streaming,
            ...(event.payload.phase !== undefined ? { phase: event.payload.phase } : {}),
            sequence: event.sequence,
            createdAt: event.payload.createdAt,
            updatedAt: event.payload.updatedAt,
          });
          const existingMessage = thread.messages.find((entry) => entry.id === message.id);
          const messages = existingMessage
            ? thread.messages.map((entry) =>
                entry.id !== message.id
                  ? entry
                  : {
                      ...entry,
                      // Always append `message.text`; the decider emits `text: ""` for the
                      // terminal `streaming=false` event, so concatenation is safe and preserves
                      // any deltas that were already accumulated by earlier batches.
                      text: `${entry.text}${message.text}`,
                      streaming: message.streaming,
                      ...(message.turnId !== undefined ? { turnId: message.turnId } : {}),
                      ...(message.phase !== undefined ? { phase: message.phase } : {}),
                      ...(message.streaming
                        ? entry.completedAt !== undefined
                          ? { completedAt: entry.completedAt }
                          : {}
                        : message.completedAt !== undefined
                          ? { completedAt: message.completedAt }
                          : {}),
                      ...(message.attachments !== undefined
                        ? { attachments: message.attachments }
                        : {}),
                    },
              )
            : [...thread.messages, message];
          const cappedMessages = messages.slice(-MAX_THREAD_MESSAGES);
          const latestTurn: Thread["latestTurn"] =
            event.payload.role === "assistant" &&
            event.payload.turnId !== null &&
            (thread.latestTurn === null || thread.latestTurn.turnId === event.payload.turnId)
              ? {
                  turnId: event.payload.turnId,
                  state: event.payload.streaming
                    ? "running"
                    : thread.latestTurn?.state === "interrupted"
                      ? "interrupted"
                      : thread.latestTurn?.state === "error"
                        ? "error"
                        : "completed",
                  requestedAt:
                    thread.latestTurn?.turnId === event.payload.turnId
                      ? thread.latestTurn.requestedAt
                      : event.payload.createdAt,
                  startedAt:
                    thread.latestTurn?.turnId === event.payload.turnId
                      ? (thread.latestTurn.startedAt ?? event.payload.createdAt)
                      : event.payload.createdAt,
                  completedAt:
                    thread.latestTurn?.turnId === event.payload.turnId
                      ? event.payload.streaming
                        ? (thread.latestTurn.completedAt ?? null)
                        : laterIsoTimestamp(thread.latestTurn.completedAt, event.payload.updatedAt)
                      : event.payload.streaming
                        ? null
                        : event.payload.updatedAt,
                  assistantMessageId: event.payload.messageId,
                }
              : thread.latestTurn;
          return {
            ...thread,
            messages: cappedMessages,
            latestTurn,
            updatedAt: event.occurredAt,
          };
        }),
        event.payload.threadId,
        event.payload.streaming ? null : event.payload.messageId,
      );

    case "thread.session-set":
      return updateThreadState(state, event.payload.threadId, (thread) => ({
        ...thread,
        session: mapSession(event.payload.session),
        error: sanitizeThreadErrorMessage(event.payload.session.lastError),
        latestTurn:
          event.payload.session.status === "running" && event.payload.session.activeTurnId !== null
            ? {
                turnId: event.payload.session.activeTurnId,
                state: "running",
                requestedAt:
                  thread.latestTurn?.turnId === event.payload.session.activeTurnId
                    ? thread.latestTurn.requestedAt
                    : event.payload.session.updatedAt,
                startedAt:
                  thread.latestTurn?.turnId === event.payload.session.activeTurnId
                    ? (thread.latestTurn.startedAt ?? event.payload.session.updatedAt)
                    : event.payload.session.updatedAt,
                completedAt: null,
                assistantMessageId:
                  thread.latestTurn?.turnId === event.payload.session.activeTurnId
                    ? thread.latestTurn.assistantMessageId
                    : null,
              }
            : thread.latestTurn,
        updatedAt: event.occurredAt,
      }));

    case "thread.turn-settled":
      return updateThreadState(state, event.payload.threadId, (thread) => {
        const latestTurn = thread.latestTurn;
        if (latestTurn === null || latestTurn.turnId !== event.payload.turnId) {
          return thread;
        }
        return {
          ...thread,
          latestTurn: {
            turnId: latestTurn.turnId,
            state: latestTurn.state === "running" ? event.payload.state : latestTurn.state,
            requestedAt: latestTurn.requestedAt,
            startedAt: latestTurn.startedAt ?? event.payload.createdAt,
            completedAt: laterIsoTimestamp(latestTurn.completedAt, event.payload.createdAt),
            assistantMessageId: latestTurn.assistantMessageId,
          },
          updatedAt: event.occurredAt,
        };
      });

    case "thread.session-stop-requested":
      return updateThreadState(state, event.payload.threadId, (thread) =>
        thread.session === null
          ? thread
          : {
              ...thread,
              session: {
                ...thread.session,
                status: "closed",
                orchestrationStatus: "stopped",
                activeTurnId: undefined,
                updatedAt: event.payload.createdAt,
              },
              updatedAt: event.occurredAt,
            },
      );

    case "thread.activity-appended":
      return updateThreadState(state, event.payload.threadId, (thread) => {
        const activities = capThreadActivities(
          [
            ...thread.activities.filter((activity) => activity.id !== event.payload.activity.id),
            { ...event.payload.activity, sequence: event.sequence },
          ].toSorted(compareActivities),
        );
        return {
          ...thread,
          activities,
          updatedAt: event.occurredAt,
        };
      });
  }

  return state;
}

const shellTextEncoder = new TextEncoder();
const compareShellText = Order.mapInput(Order.Array(Order.Number), (value: string) =>
  Array.from(shellTextEncoder.encode(value)),
);
const compareShellCreation = (
  left: { readonly id: string; readonly createdAt?: string | undefined },
  right: { readonly id: string; readonly createdAt?: string | undefined },
) =>
  compareShellText(left.createdAt ?? "", right.createdAt ?? "") ||
  compareShellText(left.id, right.id);

function applyEnvironmentShellEvent(
  state: EnvironmentState,
  event: ClientOrchestrationShellStreamEvent,
  environmentId: EnvironmentId,
): EnvironmentState {
  switch (event.kind) {
    case "project-upserted": {
      const nextProject = mapProject(event.project, environmentId);
      const existingProjectId =
        state.projectIds.find(
          (projectId) =>
            projectId === event.project.id ||
            state.projectById[projectId]?.cwd === event.project.workspaceRoot,
        ) ?? null;
      let projectById = state.projectById;
      let projectIds = state.projectIds;

      if (existingProjectId !== null && existingProjectId !== nextProject.id) {
        const { [existingProjectId]: _removedProject, ...restProjectById } = state.projectById;
        projectById = {
          ...restProjectById,
          [nextProject.id]: nextProject,
        };
        projectIds = state.projectIds.map((projectId) =>
          projectId === existingProjectId ? nextProject.id : projectId,
        );
      } else {
        projectById = {
          ...state.projectById,
          [nextProject.id]: nextProject,
        };
        projectIds =
          existingProjectId === null && !state.projectIds.includes(nextProject.id)
            ? [...state.projectIds, nextProject.id]
            : state.projectIds;
      }

      return {
        ...state,
        projectById,
        projectIds: projectIds.toSorted((left, right) =>
          compareShellCreation(projectById[left]!, projectById[right]!),
        ),
      };
    }
    case "project-removed": {
      if (!state.projectById[event.projectId]) {
        return state;
      }
      const { [event.projectId]: _removedProject, ...projectById } = state.projectById;
      return {
        ...state,
        projectById,
        projectIds: removeId(state.projectIds, event.projectId),
      };
    }
    case "thread-upserted": {
      const previous = state.threadShellById[event.thread.id];
      const nextState = writeThreadShellState(state, mapThreadShell(event.thread, environmentId));
      if (
        (previous?.projectId === event.thread.projectId &&
          previous.createdAt === event.thread.createdAt) ||
        !nextState.threadIds.includes(event.thread.id)
      )
        return nextState;
      const compareThreads = (leftId: ThreadId, rightId: ThreadId) => {
        const left = nextState.threadShellById[leftId]!;
        const right = nextState.threadShellById[rightId]!;
        return (
          compareShellText(left.projectId, right.projectId) || compareShellCreation(left, right)
        );
      };
      return {
        ...nextState,
        threadIds: nextState.threadIds.toSorted(compareThreads),
        threadIdsByProjectId: {
          ...nextState.threadIdsByProjectId,
          [event.thread.projectId]: (
            nextState.threadIdsByProjectId[event.thread.projectId] ?? []
          ).toSorted(compareThreads),
        },
      };
    }
    case "thread-removed":
      return removeThreadState(state, event.threadId);
  }
}

export function applyOrchestrationEvents(
  state: AppState,
  events: ReadonlyArray<ClientOrchestrationEvent>,
  environmentId: EnvironmentId,
): AppState {
  if (events.length === 0) {
    return state;
  }
  const currentEnvironmentState = getStoredEnvironmentState(state, environmentId);
  const nextEnvironmentState = events.reduce(
    (nextState, event) => applyEnvironmentOrchestrationEvent(nextState, event, environmentId),
    currentEnvironmentState,
  );
  return commitEnvironmentState(state, environmentId, nextEnvironmentState);
}

function getEnvironmentEntries(
  state: AppState,
): ReadonlyArray<readonly [EnvironmentId, EnvironmentState]> {
  return Object.entries(state.environmentStateById) as unknown as ReadonlyArray<
    readonly [EnvironmentId, EnvironmentState]
  >;
}

export function selectEnvironmentState(
  state: AppState,
  environmentId: EnvironmentId | null | undefined,
): EnvironmentState {
  return environmentId ? getStoredEnvironmentState(state, environmentId) : initialEnvironmentState;
}

export function selectProjectsForEnvironment(
  state: AppState,
  environmentId: EnvironmentId | null | undefined,
): Project[] {
  return getProjects(selectEnvironmentState(state, environmentId));
}

export function selectThreadsForEnvironment(
  state: AppState,
  environmentId: EnvironmentId | null | undefined,
): Thread[] {
  return getThreads(selectEnvironmentState(state, environmentId));
}

export function selectProjectsAcrossEnvironments(state: AppState): Project[] {
  return getEnvironmentEntries(state).flatMap(([, environmentState]) =>
    getProjects(environmentState),
  );
}

export function selectThreadsAcrossEnvironments(state: AppState): Thread[] {
  return getEnvironmentEntries(state).flatMap(([, environmentState]) =>
    getThreads(environmentState),
  );
}

/** Like `selectThreadsAcrossEnvironments` but returns stable `ThreadShell` references from the store (no derived data). */
export function selectThreadShellsAcrossEnvironments(state: AppState): ThreadShell[] {
  return getEnvironmentEntries(state).flatMap(([, environmentState]) =>
    environmentState.threadIds.flatMap((threadId) => {
      const shell = environmentState.threadShellById[threadId];
      return shell ? [shell] : [];
    }),
  );
}

export function selectSidebarThreadsAcrossEnvironments(state: AppState): SidebarThreadSummary[] {
  return getEnvironmentEntries(state).flatMap(([environmentId, environmentState]) =>
    environmentState.threadIds.flatMap((threadId) => {
      const thread = environmentState.sidebarThreadSummaryById[threadId];
      return thread && thread.environmentId === environmentId ? [thread] : [];
    }),
  );
}

export function selectSidebarThreadsForProjectRef(
  state: AppState,
  ref: ScopedProjectRef | null | undefined,
): SidebarThreadSummary[] {
  if (!ref) {
    return [];
  }

  const environmentState = selectEnvironmentState(state, ref.environmentId);
  const threadIds = environmentState.threadIdsByProjectId[ref.projectId] ?? EMPTY_THREAD_IDS;
  return threadIds.flatMap((threadId) => {
    const thread = environmentState.sidebarThreadSummaryById[threadId];
    return thread ? [thread] : [];
  });
}

export function selectSidebarThreadsForProjectRefs(
  state: AppState,
  refs: readonly ScopedProjectRef[],
): SidebarThreadSummary[] {
  if (refs.length === 0) return [];
  if (refs.length === 1) return selectSidebarThreadsForProjectRef(state, refs[0]);
  return refs.flatMap((ref) => selectSidebarThreadsForProjectRef(state, ref));
}

export function selectBootstrapCompleteForActiveEnvironment(state: AppState): boolean {
  return selectEnvironmentState(state, state.activeEnvironmentId).bootstrapComplete;
}

export function selectProjectByRef(
  state: AppState,
  ref: ScopedProjectRef | null | undefined,
): Project | undefined {
  return ref
    ? selectEnvironmentState(state, ref.environmentId).projectById[ref.projectId]
    : undefined;
}

/**
 * Direct sub-agent children of a thread, oldest first. Sub-agent shells stay
 * out of the sidebar indices, so they are only reachable through the shell map.
 */
export function selectSubAgentChildren(
  state: AppState,
  ref: ScopedThreadRef | null | undefined,
): ThreadShell[] {
  if (!ref) {
    return [];
  }
  const environmentState = selectEnvironmentState(state, ref.environmentId);
  return Object.values(environmentState.threadShellById)
    .filter((shell) => shell.parentThreadId === ref.threadId)
    .toSorted((left, right) => left.createdAt.localeCompare(right.createdAt));
}

/**
 * Status of a single sub-agent child from the same level signal the work-log
 * rows and the sub-agent panel header read: the child thread's session and
 * turn state.
 */
export function selectSubAgentChildStatus(
  state: AppState,
  ref: ScopedThreadRef | null | undefined,
): ChildTaskStatus | null {
  if (!ref) {
    return null;
  }
  const environmentState = selectEnvironmentState(state, ref.environmentId);
  return deriveChildTaskStatus(
    environmentState.threadTurnStateById[ref.threadId]?.latestTurn,
    environmentState.threadSessionById[ref.threadId],
  );
}

export function selectSubAgentChildTurn(
  state: AppState,
  ref: ScopedThreadRef | null | undefined,
): OrchestrationLatestTurn | null {
  if (!ref) {
    return null;
  }
  return (
    selectEnvironmentState(state, ref.environmentId).threadTurnStateById[ref.threadId]
      ?.latestTurn ?? null
  );
}

/**
 * Counts of a thread's direct sub-agent children as primitives — a number
 * keeps `useStore` subscriptions from re-rendering on every unrelated store
 * commit, which an array or an object identity would not.
 */
export function selectRunningSubAgentChildCount(
  state: AppState,
  ref: ScopedThreadRef | null | undefined,
): number {
  if (!ref) {
    return 0;
  }
  const environmentState = selectEnvironmentState(state, ref.environmentId);
  let count = 0;
  for (const shell of Object.values(environmentState.threadShellById)) {
    if (shell.parentThreadId !== ref.threadId) {
      continue;
    }
    const status = deriveChildTaskStatus(
      environmentState.threadTurnStateById[shell.id]?.latestTurn,
      environmentState.threadSessionById[shell.id],
    );
    if (status === "running") {
      count += 1;
    }
  }
  return count;
}

export function selectThreadByRef(
  state: AppState,
  ref: ScopedThreadRef | null | undefined,
): Thread | undefined {
  if (!ref) {
    return undefined;
  }
  const environmentState = selectEnvironmentState(state, ref.environmentId);
  const thread = getThreadFromEnvironmentState(environmentState, ref.threadId);
  return thread === undefined
    ? undefined
    : withLiveMessages(thread, environmentState.liveMessagesByThreadId[ref.threadId]);
}

export function selectThreadExistsByRef(
  state: AppState,
  ref: ScopedThreadRef | null | undefined,
): boolean {
  return ref
    ? selectEnvironmentState(state, ref.environmentId).threadShellById[ref.threadId] !== undefined
    : false;
}

export function selectSidebarThreadSummaryByRef(
  state: AppState,
  ref: ScopedThreadRef | null | undefined,
): SidebarThreadSummary | undefined {
  return ref
    ? selectEnvironmentState(state, ref.environmentId).sidebarThreadSummaryById[ref.threadId]
    : undefined;
}

export function selectThreadIdsByProjectRef(
  state: AppState,
  ref: ScopedProjectRef | null | undefined,
): ThreadId[] {
  return ref
    ? (selectEnvironmentState(state, ref.environmentId).threadIdsByProjectId[ref.projectId] ??
        EMPTY_THREAD_IDS)
    : EMPTY_THREAD_IDS;
}

export function setError(state: AppState, threadId: ThreadId, error: string | null): AppState {
  if (state.activeEnvironmentId === null) {
    return state;
  }

  const nextEnvironmentState = updateThreadState(
    getStoredEnvironmentState(state, state.activeEnvironmentId),
    threadId,
    (thread) => {
      if (thread.error === error) return thread;
      return { ...thread, error };
    },
  );
  return commitEnvironmentState(state, state.activeEnvironmentId, nextEnvironmentState);
}

export function applyOrchestrationEvent(
  state: AppState,
  event: ClientOrchestrationEvent,
  environmentId: EnvironmentId,
): AppState {
  return commitEnvironmentState(
    state,
    environmentId,
    applyEnvironmentOrchestrationEvent(
      getStoredEnvironmentState(state, environmentId),
      event,
      environmentId,
    ),
  );
}

export function applyShellEvent(
  state: AppState,
  event: ClientOrchestrationShellStreamEvent,
  environmentId: EnvironmentId,
): AppState {
  return commitEnvironmentState(
    state,
    environmentId,
    applyEnvironmentShellEvent(
      getStoredEnvironmentState(state, environmentId),
      event,
      environmentId,
    ),
  );
}

export function setActiveEnvironmentId(state: AppState, environmentId: EnvironmentId): AppState {
  if (state.activeEnvironmentId === environmentId) {
    return state;
  }

  return {
    ...state,
    activeEnvironmentId: environmentId,
  };
}

export function removeEnvironmentState(state: AppState, environmentId: EnvironmentId): AppState {
  if (!state.environmentStateById[environmentId] && state.activeEnvironmentId !== environmentId) {
    return state;
  }

  const { [environmentId]: _removed, ...environmentStateById } = state.environmentStateById;
  return {
    ...state,
    activeEnvironmentId:
      state.activeEnvironmentId === environmentId ? null : state.activeEnvironmentId,
    environmentStateById,
  };
}

export function syncThreadRail(
  state: AppState,
  environmentId: EnvironmentId,
  threadId: ThreadId,
  update: UserMessageRailUpdate,
): AppState {
  const env = getStoredEnvironmentState(state, environmentId);
  return commitEnvironmentState(state, environmentId, {
    ...env,
    userMessageRailByThreadId: {
      ...env.userMessageRailByThreadId,
      [threadId]: applyUserMessageRailUpdate(env.userMessageRailByThreadId[threadId] ?? [], update),
    },
  });
}

interface AppStore extends AppState {
  syncThreadRail: (
    environmentId: EnvironmentId,
    threadId: ThreadId,
    update: UserMessageRailUpdate,
  ) => void;
  setActiveEnvironmentId: (environmentId: EnvironmentId) => void;
  removeEnvironmentState: (environmentId: EnvironmentId) => void;
  syncServerShellSnapshot: (
    snapshot: ClientOrchestrationShellSnapshot,
    environmentId: EnvironmentId,
  ) => void;
  syncServerThreadDetail: (
    thread: ClientOrchestrationThread,
    environmentId: EnvironmentId,
    messageWindow: OrchestrationThreadMessageWindow,
    activityWindow: OrchestrationThreadActivityWindow,
    liveMessages: ReadonlyArray<OrchestrationLiveMessage>,
  ) => void;
  applyLiveMessageFrame: (
    frame: OrchestrationLiveMessageFrame,
    environmentId: EnvironmentId,
  ) => void;
  prependOlderMessages: (
    threadId: ThreadId,
    environmentId: EnvironmentId,
    page: { messages: ChatMessage[]; hasMoreBefore: boolean; oldestCursor: MessageCursor | null },
  ) => void;
  beginLoadingOlder: (threadId: ThreadId, environmentId: EnvironmentId) => void;
  prependOlderActivities: (
    threadId: ThreadId,
    environmentId: EnvironmentId,
    page: {
      activities: ClientOrchestrationThreadActivity[];
      hasMoreBefore: boolean;
      oldestCursor: ActivityCursor | null;
    },
  ) => void;
  beginLoadingOlderActivities: (threadId: ThreadId, environmentId: EnvironmentId) => void;
  setActivityPayload: (
    threadId: ThreadId,
    environmentId: EnvironmentId,
    activityId: string,
    payload: unknown,
  ) => void;
  clearThreadDetail: (threadId: ThreadId, environmentId: EnvironmentId) => void;
  applyOrchestrationEvent: (event: ClientOrchestrationEvent, environmentId: EnvironmentId) => void;
  applyOrchestrationEvents: (
    events: ReadonlyArray<ClientOrchestrationEvent>,
    environmentId: EnvironmentId,
  ) => void;
  applyShellEvent: (
    event: ClientOrchestrationShellStreamEvent,
    environmentId: EnvironmentId,
  ) => void;
  setError: (threadId: ThreadId, error: string | null) => void;
}

export const useStore = create<AppStore>((set) => ({
  ...initialState,
  setActiveEnvironmentId: (environmentId) =>
    set((state) => setActiveEnvironmentId(state, environmentId)),
  removeEnvironmentState: (environmentId) =>
    set((state) => removeEnvironmentState(state, environmentId)),
  syncThreadRail: (environmentId, threadId, update) =>
    set((state) => syncThreadRail(state, environmentId, threadId, update)),
  syncServerShellSnapshot: (snapshot, environmentId) =>
    set((state) => syncServerShellSnapshot(state, snapshot, environmentId)),
  syncServerThreadDetail: (thread, environmentId, messageWindow, activityWindow, liveMessages) =>
    set((state) =>
      syncServerThreadDetail(
        state,
        thread,
        environmentId,
        messageWindow,
        activityWindow,
        liveMessages,
      ),
    ),
  applyLiveMessageFrame: (frame, environmentId) =>
    set((state) => applyLiveMessageFrameToState(state, environmentId, frame)),
  prependOlderMessages: (threadId, environmentId, page) =>
    set((state) => prependOlderMessages(state, threadId, environmentId, page)),
  beginLoadingOlder: (threadId, environmentId) =>
    set((state) => beginLoadingOlder(state, threadId, environmentId)),
  prependOlderActivities: (threadId, environmentId, page) =>
    set((state) => prependOlderActivities(state, threadId, environmentId, page)),
  beginLoadingOlderActivities: (threadId, environmentId) =>
    set((state) => beginLoadingOlderActivities(state, threadId, environmentId)),
  setActivityPayload: (threadId, environmentId, activityId, payload) =>
    set((state) => setActivityPayload(state, threadId, environmentId, activityId, payload)),
  clearThreadDetail: (threadId, environmentId) =>
    set((state) => clearThreadDetail(state, threadId, environmentId)),
  applyOrchestrationEvent: (event, environmentId) =>
    set((state) => applyOrchestrationEvent(state, event, environmentId)),
  applyOrchestrationEvents: (events, environmentId) =>
    set((state) => applyOrchestrationEvents(state, events, environmentId)),
  applyShellEvent: (event, environmentId) =>
    set((state) => applyShellEvent(state, event, environmentId)),
  setError: (threadId, error) => set((state) => setError(state, threadId, error)),
}));
